import { Lock } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import * as React from 'react';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';

import { shortFormatDate } from '@/utils/dates';
import { getAuctioneerLogoUrl, getAuctioneerLogoUrlPlaceholder } from '@/utils/urls';

type Props = {
    completedReview: any;
    reviewComments: any;
    seller: any;
};

const ReviewedPageComments = (props: Props) => {
    const { completedReview, reviewComments, seller } = props;
    const { note, review, reviewHeadline } = completedReview;

    const logoUrl = seller.logoId ? getAuctioneerLogoUrl(seller.logoId, 48) : '';
    const logoUrlPlaceholder = seller.logoId ? getAuctioneerLogoUrlPlaceholder(seller.logoId, 48) : '';

    const publicComments =
        Boolean(reviewComments) &&
        reviewComments
            .filter((rc) => rc.privacyStatus === 'public')
            .map((rc) => {
                return (
                    <StyledCommentResponse
                        aria-label="public"
                        key={rc.commentId}
                    >
                        <StyledLogoContainer>
                            <StyledHouseLogo>
                                <img
                                    alt={`${rc.userDisplayName} Logo`}
                                    //@ts-expect-error
                                    placeholder={logoUrlPlaceholder}
                                    src={logoUrl}
                                />
                            </StyledHouseLogo>
                        </StyledLogoContainer>
                        <StyleCommentContainer>
                            <StyledCommentResponseHeader>
                                <StyledCommentDisplayName>{rc.userDisplayName}</StyledCommentDisplayName>
                                <StyledCommentTime>{shortFormatDate(rc.commentSubmittedTS)}</StyledCommentTime>
                            </StyledCommentResponseHeader>
                            <StyledCommentText>{rc.comment}</StyledCommentText>
                        </StyleCommentContainer>
                    </StyledCommentResponse>
                );
            });

    const privateComments =
        Boolean(reviewComments) &&
        reviewComments
            .filter((rc) => rc.privacyStatus === 'private')
            .map((rc) => {
                return (
                    <StyledCommentResponse
                        aria-label="private"
                        key={rc.commentId}
                    >
                        <StyledLogoContainer>
                            <StyledHouseLogo>
                                <img
                                    alt={`${rc.userDisplayName} Logo`}
                                    src={logoUrl}
                                />
                            </StyledHouseLogo>
                        </StyledLogoContainer>
                        <StyleCommentContainer>
                            <StyledCommentResponseHeader>
                                <StyledCommentDisplayName>{rc.userDisplayName}</StyledCommentDisplayName>
                                <StyledCommentTime>{shortFormatDate(rc.commentSubmittedTS)}</StyledCommentTime>
                            </StyledCommentResponseHeader>
                            <StyledCommentText>{rc.comment}</StyledCommentText>
                        </StyleCommentContainer>
                    </StyledCommentResponse>
                );
            });

    const showPublic = Boolean(review) || Boolean(reviewHeadline) || publicComments;
    const showPrivate = Boolean(note) || privateComments;

    return (
        <StyledCommentsContainer data-testid="comments-container">
            <StyledComments>
                {showPublic && (
                    <StyledPublicReview aria-label="public-review">
                        <h2>
                            <FormattedMessage id="review_page.labels.public_review" />
                        </h2>
                        {reviewHeadline && <StyledReviewHeadline>{reviewHeadline}</StyledReviewHeadline>}
                        {review && <StyledReviewText>{review}</StyledReviewText>}
                        {publicComments}
                    </StyledPublicReview>
                )}
                {showPrivate && (
                    <div>
                        <h2>
                            <FormattedMessage id="review_page.labels.private_feedback" />{' '}
                        </h2>
                        <StyledPrivateFeedbackContainer aria-label="private-review">
                            <StyledPrivateFeedbackSection>
                                {note && <StyledReviewText>{note}</StyledReviewText>}
                                {privateComments}
                                <StyledPrivateLabel>
                                    <FormattedMessage id="review_page.labels.private_feedback_label" />
                                    <Lock
                                        className="ml-2xs inline-block"
                                        size="xs"
                                        style="fill"
                                    />
                                </StyledPrivateLabel>
                            </StyledPrivateFeedbackSection>
                        </StyledPrivateFeedbackContainer>
                    </div>
                )}
            </StyledComments>
        </StyledCommentsContainer>
    );
};

export default React.memo<Props>(ReviewedPageComments, isEqual);

const StyledCommentResponse = styled.div`
    align-items: flex-start;
    border-top: 1px solid ${({ theme }) => theme.colors.grey400};
    display: flex;
    flex-direction: row;
    margin-top: 14px;
    padding: 15px 0;
`;

const StyledLogoContainer = styled.div`
    background-color: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.grey400};
    height: 48px;
    margin-right: 20px;
    width: 48px;
`;

const StyledHouseLogo = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 48px;
    max-height: 100%;
    max-width: 100%;
    width: 48px;

    img {
        max-width: 95%;
        max-height: 95%;
    }
`;

const StyleCommentContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const StyledCommentResponseHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const StyledCommentDisplayName = styled.span`
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
`;

const StyledCommentTime = styled.span`
    color: ${({ theme }) => theme.colors.grey100};
`;

const StyledCommentText = styled.div`
    margin-top: 10px;
    word-break: break-word;
`;

const StyledCommentsContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    margin-top: 17px;
    padding-bottom: 30px;
`;

const StyledComments = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: calc(100% - 273px);

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        width: 100%;
    }
`;

const StyledPublicReview = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledReviewHeadline = styled.span`
    max-width: 700px;
    font-weight: 700;
    margin-top: 8px;
`;

const StyledReviewText = styled.span`
    line-height: 24px;
    margin: 4px 0 5px;
    max-width: 700px;
`;

const StyledPrivateFeedbackContainer = styled.div`
    background-color: ${({ theme }) => theme.colors.grey500};
    flex-direction: row;
    margin-top: 16px;
    padding: 10px 23px 16px;
    width: 83%;

    @media (max-width: 830px) {
        width: 100%;
    }
`;

const StyledPrivateFeedbackSection = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledPrivateLabel = styled.div`
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    font-size: 11px;
    letter-spacing: 1px;
    margin-left: auto;
    display: flex;
    align-items: center;
    padding: 12px 0 0;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.grey100};
`;
